import { useAtomValue } from 'jotai';
import { useMemo } from 'react';

import { participantsByIdAtom } from '../../video-sdk';
import { joiningVisitorIdFamily, visitorAdmissionTimesAtom, waitingVisitorsAtom } from '../state';

export type AdmittedClientStatus =
  | 'sending_invite'
  | 'waiting_for_click'
  | 'preparing'
  | 'joining'
  | 'not_on_page'
  | 'connected';

export type AdmittedClientStatusResult =
  | {
      status: 'connected';
      showSpinner: false;
      shouldPulse: false;
    }
  | {
      status: 'not_on_page';
      showSpinner: false;
      shouldPulse: true;
    }
  | {
      status: Exclude<AdmittedClientStatus, 'connected' | 'not_on_page'>;
      showSpinner: true;
      shouldPulse: false;
    };

interface UseAdmittedClientStatusProps {
  visitorUuid: string;
  /**
   * When false (default), uses the legacy two-state logic:
   *   "Waiting for [name] to click Join" → "[name] is connecting…"
   *
   * Set to true when the feature flag is on, regardless of join mode. Every
   * client relays isInForeground and isJoining on each presence heartbeat, so
   * both manual and auto-join resolve to real statuses. Only the admissionTime
   * echo is manual-only, which leaves "waiting_for_click" unreachable for
   * auto-join.
   */
  granularStatusEnabled?: boolean;
}

export function useAdmittedClientStatus({
  visitorUuid,
  granularStatusEnabled = false,
}: UseAdmittedClientStatusProps): AdmittedClientStatusResult {
  const waitingVisitors = useAtomValue(waitingVisitorsAtom);
  const visitorAdmissionTimes = useAtomValue(visitorAdmissionTimesAtom);
  const participantsById = useAtomValue(participantsByIdAtom);
  const isAdmitting = useAtomValue(joiningVisitorIdFamily(visitorUuid));

  const waitingVisitor = waitingVisitors[visitorUuid];
  const providerAdmissionTime = visitorAdmissionTimes?.[visitorUuid];
  const visitorAdmissionTime = waitingVisitor?.admissionTime;
  const hasParticipant = Boolean(participantsById[visitorUuid]);
  const isInForeground = waitingVisitor?.isInForeground;
  const isJoining = Boolean(waitingVisitor?.isJoining);

  return useMemo(() => {
    if (hasParticipant) {
      return {
        status: 'connected',
        showSpinner: false,
        shouldPulse: false,
      };
    }

    // Legacy path: flag off. We fall back to the simple two-state treatment
    // regardless of join mode.
    if (!granularStatusEnabled) {
      if (!waitingVisitor) {
        return {
          status: 'waiting_for_click',
          showSpinner: true,
          shouldPulse: false,
        };
      }

      if (providerAdmissionTime && visitorAdmissionTime === providerAdmissionTime) {
        return {
          status: 'joining',
          showSpinner: true,
          shouldPulse: false,
        };
      }

      return {
        status: 'waiting_for_click',
        showSpinner: true,
        shouldPulse: false,
      };
    }

    // Granular path: flag on, either join mode.
    if (isJoining) {
      return {
        status: 'preparing',
        showSpinner: true,
        shouldPulse: false,
      };
    }

    if (isInForeground === false) {
      return {
        status: 'not_on_page',
        showSpinner: false,
        shouldPulse: true,
      };
    }

    if (!waitingVisitor) {
      return {
        status: 'not_on_page',
        showSpinner: false,
        shouldPulse: true,
      };
    }

    if (providerAdmissionTime && visitorAdmissionTime === providerAdmissionTime) {
      return {
        status: 'waiting_for_click',
        showSpinner: true,
        shouldPulse: false,
      };
    }

    // Show "Sending Invitation…" from the moment the admit button is clicked
    // (isAdmitting is true before the mutation resolves) through until the client
    // echoes the admissionTime back. Falls through to "joining" ("[name] is
    // connecting…") only for old manual-mode clients that never send an
    // admissionTime echo.
    if (isAdmitting || providerAdmissionTime) {
      return {
        status: 'sending_invite',
        showSpinner: true,
        shouldPulse: false,
      };
    }

    return {
      status: 'joining',
      showSpinner: true,
      shouldPulse: false,
    };
  }, [
    granularStatusEnabled,
    hasParticipant,
    isAdmitting,
    isInForeground,
    isJoining,
    providerAdmissionTime,
    visitorAdmissionTime,
    waitingVisitor,
  ]);
}
